import { useState } from "react";
import { FileSpreadsheet, ListChecks } from "lucide-react";
import { Modal } from "#/components/common/Modal";
import { inventoryApi } from "../api";
import { InventoryPageHeader } from "../components/InventoryPageHeader";
import { InventoryPagination } from "../components/InventoryPagination";
import { InventoryStatusBadge } from "../components/InventoryStatusBadge";
import { InventoryError, InventoryLoading } from "../components/InventoryState";
import { InventoryUploadPanel } from "../components/InventoryUploadPanel";
import { useAsyncData, usePageState } from "../hooks";
import type { InventoryImportBatchSummary, InventoryImportBatchStatus } from "../types";

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

const STATUS_TABS: { label: string; value: InventoryImportBatchStatus | "ALL" | "PENDING_APPROVAL_INBOX" }[] = [
  { label: "All",              value: "ALL" },
  { label: "Validated",        value: "VALIDATED" },
  { label: "Pending Approval", value: "PENDING_APPROVAL_INBOX" },
  { label: "Approved",         value: "APPROVED" },
  { label: "Posted",           value: "POSTED" },
  { label: "Rejected",         value: "REJECTED" },
];

function fmtDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-KE", { dateStyle: "short", timeStyle: "short" });
}

export default function InventoryImportPage() {
  const { page, size, setPage, setSize } = usePageState(10);
  const [statusFilter, setStatusFilter] = useState<InventoryImportBatchStatus | "ALL" | "PENDING_APPROVAL_INBOX">("ALL");

  const isInbox = statusFilter === "PENDING_APPROVAL_INBOX";

  const { data, loading, error, reload } = useAsyncData(
    () => isInbox
      ? inventoryApi.listPendingApproval(page, size)
      : inventoryApi.listImports(page, size, statusFilter === "ALL" ? undefined : statusFilter as string),
    [page, size, statusFilter],
  );

  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowsBatch, setRowsBatch] = useState<InventoryImportBatchSummary | null>(null);
  const [exportingErrorsId, setExportingErrorsId] = useState<number | null>(null);

  async function downloadErrorReport(batch: InventoryImportBatchSummary) {
    setExportingErrorsId(batch.id);
    try {
      downloadBlob(await inventoryApi.downloadImportErrorReport(batch.id), `${batch.batchNo}-errors.xlsx`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to download error report");
    } finally {
      setExportingErrorsId(null);
    }
  }

  async function runAction(batchId: number, action: () => Promise<InventoryImportBatchSummary>) {
    try {
      setActionError(null);
      setBusyId(batchId);
      await action();
      await reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusyId(null);
    }
  }

  function handleTabChange(value: typeof statusFilter) {
    setStatusFilter(value);
    setPage(0);
  }

  return (
    <div className="space-y-6">
      <InventoryPageHeader
        title="Inventory Imports"
      />

      <InventoryUploadPanel />

      <div className="rounded-2xl border bg-white shadow-sm">
        <div className="border-b px-5 py-4">
          <h2 className="font-bold text-slate-900">Import Batches</h2>
        </div>

        {/* Status filter tabs */}
        <div className="border-b px-5 py-2 flex flex-wrap gap-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => handleTabChange(t.value)}
              className={[
                "rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                statusFilter === t.value
                  ? "bg-emerald-700 text-white"
                  : "text-slate-600 hover:bg-slate-100",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </div>

        {actionError && (
          <div className="mx-5 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {actionError}
          </div>
        )}

        {loading && <div className="p-5"><InventoryLoading label="Loading import batches..." /></div>}
        {error && <div className="p-5"><InventoryError message={error} /></div>}

        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Batch No.</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Uploaded By</th>
                  <th className="px-4 py-3">Uploaded At</th>
                  <th className="px-4 py-3">Valid</th>
                  <th className="px-4 py-3">Invalid</th>
                  <th className="px-4 py-3">Remarks</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data?.content ?? []).map((batch) => (
                  <tr key={batch.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-900">{batch.batchNo}</td>
                    <td className="px-4 py-3 text-slate-600">{batch.batchType.replace("_", " ")}</td>
                    <td className="px-4 py-3"><InventoryStatusBadge value={batch.status} /></td>
                    <td className="px-4 py-3 text-slate-600">{batch.uploadedByUsername ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(batch.uploadedAt)}</td>
                    <td className="px-4 py-3 text-emerald-700 font-semibold">{batch.validRows}</td>
                    <td className="px-4 py-3 text-red-600 font-semibold">{batch.invalidRows}</td>
                    <td className="px-4 py-3 max-w-[160px] truncate text-slate-500" title={batch.remarks ?? ""}>
                      {batch.remarks ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <BatchActions
                        batch={batch}
                        busy={busyId === batch.id}
                        onAction={runAction}
                        onViewRows={() => setRowsBatch(batch)}
                        onDownloadErrors={() => downloadErrorReport(batch)}
                        exportingErrors={exportingErrorsId === batch.id}
                      />
                    </td>
                  </tr>
                ))}
                {(data?.content ?? []).length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-slate-500">
                      {isInbox ? "No batches pending your approval." : "No import batches found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 pb-4">
          <InventoryPagination page={page} setPage={setPage} size={size} setSize={setSize} data={data} />
        </div>
      </div>

      <ImportRowsModal batch={rowsBatch} onClose={() => setRowsBatch(null)} />
    </div>
  );
}

function BatchActions({
  batch,
  busy,
  onAction,
  onViewRows,
  onDownloadErrors,
  exportingErrors,
}: {
  batch: InventoryImportBatchSummary;
  busy: boolean;
  onAction: (id: number, fn: () => Promise<InventoryImportBatchSummary>) => void;
  onViewRows: () => void;
  onDownloadErrors: () => void;
  exportingErrors: boolean;
}) {
  const btn = (label: string, fn: () => Promise<InventoryImportBatchSummary>, cls: string) => (
    <button
      key={label}
      disabled={busy}
      onClick={() => onAction(batch.id, fn)}
      className={`rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${cls}`}
    >
      {busy ? "…" : label}
    </button>
  );

  const actions: React.ReactNode[] = [];

  if (batch.status === "VALIDATED" || batch.status === "PARTIALLY_VALID") {
    actions.push(btn("Submit", () => inventoryApi.submitImport(batch.id), "border border-amber-200 text-amber-700 hover:bg-amber-50"));
  }
  if (batch.status === "PENDING_APPROVAL") {
    actions.push(btn("Recall",  () => inventoryApi.recallImport(batch.id),  "border border-slate-300 text-slate-600 hover:bg-slate-50"));
    // Approve auto-posts server-side in the same call (confirmed 2026-07-09 — the batch jumps
    // straight from PENDING_APPROVAL to POSTED). There is deliberately no separate "Post" button
    // here: a standalone post call always 409s with "Only approved batches can be posted" since
    // by the time anyone could click it, the batch is no longer sitting in APPROVED.
    actions.push(btn("Approve", () => inventoryApi.approveImport(batch.id), "border border-emerald-200 text-emerald-700 hover:bg-emerald-50"));
    actions.push(btn("Reject",  () => inventoryApi.rejectImport(batch.id),  "border border-red-200 text-red-700 hover:bg-red-50"));
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {actions}
      {batch.totalRows > 0 && (
        <button
          type="button"
          onClick={onViewRows}
          title="View row detail"
          className="flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
        >
          <ListChecks className="h-3.5 w-3.5" />
          Rows
        </button>
      )}
      {batch.invalidRows > 0 && (
        <button
          type="button"
          disabled={exportingErrors}
          onClick={onDownloadErrors}
          title="Download failed rows as Excel"
          className="flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
        >
          <FileSpreadsheet className="h-3.5 w-3.5" />
          {exportingErrors ? "…" : "Errors"}
        </button>
      )}
      {actions.length === 0 && batch.totalRows === 0 && <span className="text-slate-400">—</span>}
    </div>
  );
}

function ImportRowsModal({
  batch,
  onClose,
}: {
  batch: InventoryImportBatchSummary | null;
  onClose: () => void;
}) {
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [page, setPage] = useState(0);
  const size = 20;

  const { data, loading, error } = useAsyncData(
    () => (batch ? inventoryApi.listImportRows(batch.id, { onlyErrors, page, size }) : Promise.resolve(null)),
    [batch?.id, onlyErrors, page],
  );

  if (!batch) return null;

  return (
    <Modal
      open={!!batch}
      title={`Row Detail — ${batch.batchNo}`}
      onClose={() => { onClose(); setOnlyErrors(false); setPage(0); }}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {batch.validRows} valid · {batch.invalidRows} invalid · {batch.totalRows} total rows
          </p>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
            <input
              type="checkbox"
              checked={onlyErrors}
              onChange={(e) => { setOnlyErrors(e.target.checked); setPage(0); }}
            />
            Show errors only
          </label>
        </div>

        {loading && <InventoryLoading label="Loading rows..." />}
        {error && <InventoryError message={error} />}

        {!loading && !error && data && (
          <>
            <div className="max-h-[50vh] overflow-auto rounded-xl border">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-slate-50 uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Item Code</th>
                    <th className="px-3 py-2">Item Name</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.content.map((row) => (
                    <tr key={row.id} className={row.validRow ? "" : "bg-red-50/60"}>
                      <td className="px-3 py-2 text-slate-500">{row.rowNumber}</td>
                      <td className="px-3 py-2 font-semibold text-slate-900">{row.itemCode ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-700">{row.itemName ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-500">{row.categoryName ?? row.categoryCode ?? "—"}</td>
                      <td className="px-3 py-2">
                        {row.validRow
                          ? <span className="font-semibold text-emerald-600">Valid</span>
                          : <span className="font-semibold text-red-600">{row.errorMessage ?? "Invalid"}</span>}
                      </td>
                    </tr>
                  ))}
                  {data.content.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">No rows found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Page {page + 1} of {Math.max(data.totalPages, 1)} · {data.totalElements} rows</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="rounded-lg border px-2.5 py-1 font-semibold disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={data.last}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border px-2.5 py-1 font-semibold disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
